import { ComponentType, lazy } from 'react';

import { LS_KEY_CHUNK_RELOAD_AT } from 'src/constants/localStorageKeys';

// Minimum gap between auto-reloads — prevents an infinite loop if the fresh
// index.html still points at a missing chunk.
const CHUNK_RELOAD_COOLDOWN_MS = 10_000;

export const isChunkLoadFailure = (error: any): boolean => {
  const msg: string = error?.message ?? '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('is not a valid JavaScript MIME type') ||
    msg.includes('error loading dynamically imported module') ||
    // Server returned HTML (404/error page) instead of a JS chunk
    msg.includes('Unexpected token') ||
    // Same chunk evaluated twice due to stale/duplicate script tags
    msg.includes('has already been declared')
  );
};

// One import can report several failed deps; once this page has asked for a
// reload, later reports must not read the fresh timestamp as a cooldown.
let reloadPending = false;

// Reloads at most once per CHUNK_RELOAD_COOLDOWN_MS across the whole app —
// shared cooldown so a route-chunk failure and an api.ts chunk failure
// (or any other dynamic import) can't each trigger their own reload back-to-back.
// Returns false when the cooldown blocked the reload.
export const reloadForChunkFailure = (): boolean => {
  if (reloadPending) return true;
  const lastReload = Number(
    sessionStorage.getItem(LS_KEY_CHUNK_RELOAD_AT) ?? 0,
  );
  if (Date.now() - lastReload > CHUNK_RELOAD_COOLDOWN_MS) {
    sessionStorage.setItem(LS_KEY_CHUNK_RELOAD_AT, String(Date.now()));
    reloadPending = true;
    window.location.reload();
    return true;
  }
  return false;
};

export const reloadOnChunkLoadFailure = (error: any) => {
  if (!isChunkLoadFailure(error)) return;
  reloadForChunkFailure();
};

// React.lazy for a route/component chunk, with stale-deploy recovery.
//
// Two failure shapes have to be handled, not one:
//   * the import REJECTS — the usual stale-chunk 404, caught below;
//   * the import RESOLVES with undefined — seen on Safari, where a module that
//     failed to load does not reject. `.catch` never fires, so React reaches
//     its Resolved branch and throws an opaque
//     "undefined is not an object (evaluating 'e._result.default')" while the
//     reload recovery never runs at all.
// Both mean "this chunk did not load", so both route into the same reload.
export const lazyWithChunkReload = <T extends ComponentType<any>>(
  importFunction: () => Promise<{ default: T }>,
) =>
  lazy(() =>
    importFunction()
      .then((module) => {
        if (!module) {
          reloadForChunkFailure();
          throw new Error('Dynamic import resolved without a module');
        }
        return module;
      })
      .catch((error: any) => {
        reloadOnChunkLoadFailure(error);
        throw error;
      }),
  );
